'use client';

import { useAuth } from '@/backend/context/auth-management';
import { useOwner } from '@/backend/firebase/hooks/useOwner';
import { marketingConfigRef } from '@/backend/firebase/services/helpers/firestore-refs';
import { LegalService } from '@/backend/firebase/services/legal-service';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import type { LegalVersionWithId } from '@/shared/schema';
import {
  DiscoveryFormSchema,
  type DiscoveryFormData,
  type MarketingConfig,
} from '@/shared/schema/admin';
import { zodResolver } from '@hookform/resolvers/zod';
import { getDoc, setDoc } from 'firebase/firestore';
import {
  AlertCircle,
  Brain,
  Briefcase,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  ExternalLink,
  Loader2,
  Lock,
  Megaphone,
  ShieldCheck,
  Star,
  Target,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';

interface AdsFormData {
  adsBudget: number;
  acceptedPolicy: boolean;
}

const STEPS = [
  {
    id: 1,
    title: 'Negocio',
    description: 'Cuéntanos sobre tu marca.',
    icon: Briefcase,
    fields: [
      'businessName',
      'niche',
      'averageTicket',
    ] as (keyof DiscoveryFormData)[],
  },
  {
    id: 2,
    title: 'Operación',
    description: 'Entendamos tu capacidad.',
    icon: Zap,
    fields: [
      'returnFrequency',
      'capacityCeiling',
    ] as (keyof DiscoveryFormData)[],
  },
  {
    id: 3,
    title: 'Audiencia',
    description: '¿A quién queremos atraer?',
    icon: Users,
    fields: [
      'idealClient',
      'antiClient',
      'catalyst',
    ] as (keyof DiscoveryFormData)[],
  },
  {
    id: 4,
    title: 'Psicología',
    description: 'Motivaciones profundas.',
    icon: Brain,
    fields: [
      'priorTrauma',
      'secretFear',
      'mainObjection',
      'competition',
    ] as (keyof DiscoveryFormData)[],
  },
  {
    id: 5,
    title: 'Tu Oferta',
    description: '¿Qué te hace único?',
    icon: Megaphone,
    fields: [
      'corePromise',
      'uniqueValue',
      'currentProblem',
    ] as (keyof DiscoveryFormData)[],
  },
  {
    id: 6,
    title: 'Compromiso',
    description: 'Pacto de crecimiento.',
    icon: Target,
    fields: ['timeExpectation'] as (keyof DiscoveryFormData)[],
  },
];

export default function MarketingSettingsPage() {
  const { profile, adminSettings, marketingConfig } = useAuth();
  const ownerId = profile?.id;
  const updatePublicMutation = useOwner.updatePublic();

  const [latestPolicy, setLatestPolicy] = useState<LegalVersionWithId | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState<'discovery' | 'budget'>('discovery');
  const [currentStep, setCurrentStep] = useState(1);
  const [localMarketingConfig, setLocalMarketingConfig] = useState<
    Partial<MarketingConfig>
  >({});

  const {
    register: regDiscovery,
    control: ctrlDiscovery,
    handleSubmit: handleSubDiscovery,
    trigger: triggerDiscovery,
    setFocus: setFocusDiscovery,
    formState: { errors: errDiscovery, isSubmitting: isSubmittingDiscovery },
    reset: resetDiscovery,
  } = useForm<DiscoveryFormData>({
    resolver: zodResolver(DiscoveryFormSchema),
    defaultValues: {
      businessName: '',
      niche: '',
      averageTicket: 0,
      idealClient: '',
      antiClient: '',
      catalyst: '',
      priorTrauma: '',
      secretFear: '',
      mainObjection: '',
      currentProblem: '',
      competition: '',
      corePromise: '',
      uniqueValue: '',
      returnFrequency: '',
      capacityCeiling: '',
      timeExpectation: 'realistic',
      isCompleted: false,
    },
  });

  const nextStep = async () => {
    const fields = STEPS[currentStep - 1].fields;
    const isValid = await triggerDiscovery(fields);
    if (isValid) {
      if (currentStep < STEPS.length) {
        setCurrentStep((s) => s + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      const firstError = fields.find((f) => errDiscovery[f]);
      if (firstError) {
        setFocusDiscovery(firstError);
        toast.error('Por favor completa los campos requeridos.');
      }
    }
  };

  const prevStep = () => {
    setCurrentStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const {
    register: regAds,
    control: ctrlAds,
    handleSubmit: handleSubAds,
    watch: watchAds,
    reset: resetAds,
    formState: { isSubmitting: isSubmittingAds, isValid: isValidAds },
  } = useForm<AdsFormData>({
    defaultValues: {
      adsBudget: profile?.public?.marketing?.adsBudget || 0,
      acceptedPolicy: false,
    },
  });

  const adsBudget = watchAds('adsBudget');
  const acceptedPolicy = watchAds('acceptedPolicy');

  const isElite =
    adminSettings?.plan === 'ELITE' ||
    adminSettings?.plan === 'GROWTH' ||
    adminSettings?.features?.marketingEnabled;

  useEffect(() => {
    async function loadData() {
      if (!ownerId) return;
      try {
        // Load legal policy
        const latest = await LegalService.getLatestVersion('meta_ads_policy');
        setLatestPolicy(latest);

        // Load private marketing config from the new admin location
        const ref = marketingConfigRef(ownerId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() as Partial<MarketingConfig>;
          setLocalMarketingConfig(data);
          if (data.discovery) {
            resetDiscovery(data.discovery);
            if (data.discovery.isCompleted) {
              setView('budget');
            }
          }
        }
      } catch (e) {
        console.error('Failed to load marketing data:', e);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, [ownerId, resetDiscovery]);

  const onDiscoverySubmit = async (data: DiscoveryFormData) => {
    if (!ownerId) return;
    try {
      const finalData = { ...data, isCompleted: true };
      const ref = marketingConfigRef(ownerId);
      await setDoc(
        ref,
        {
          discovery: finalData,
          updatedAt: new Date(),
        },
        { merge: true },
      );

      setLocalMarketingConfig((prev) => ({ ...prev, discovery: finalData }));
      toast.success(
        'Formulario de auditoría enviado. Nuestro equipo lo revisará en breve.',
      );
      setView('budget');
    } catch (error) {
      toast.error('Error al guardar el formulario.');
    }
  };

  const onAdsSubmit = async (data: AdsFormData) => {
    if (!ownerId || !latestPolicy) return;

    // Check if ads are enabled by admin before allowing submission
    if (!localMarketingConfig?.isAdsEnabled) {
      toast.error(
        'Inversión de anuncios no habilitada. Contacta al administrador.',
      );
      return;
    }

    try {
      const ip = await LegalService.getIpAddress();
      await LegalService.recordAcceptance({
        userId: ownerId,
        docType: 'meta_ads_policy',
        versionId: latestPolicy.id,
        versionTag: latestPolicy.versionTag,
        ipAddress: ip,
        userAgent: navigator.userAgent,
        metadata: { authorizedBudget: data.adsBudget, currency: 'USD' },
      });

      await updatePublicMutation.mutateAsync({
        marketing: {
          ...profile?.public?.marketing,
          adsBudget: data.adsBudget,
          adsCurrency: 'USD',
        },
      });

      toast.success('Inversión autorizada y registrada legalmente.');
      resetAds(data);
    } catch (error) {
      toast.error('Error al procesar la autorización.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="text-primary h-10 w-10 animate-spin" />
      </div>
    );
  }

  if (!isElite) {
    return (
      <div className="bg-background min-h-screen pt-10 pb-32">
        <main className="mx-auto max-w-md px-4">
          <Card className="relative overflow-hidden rounded-[2.5rem] border-none bg-slate-900 p-8 text-center text-white shadow-2xl">
            <div className="absolute top-0 right-0 -mt-10 -mr-10 h-40 w-40 rounded-full bg-blue-500/20 blur-3xl" />
            <div className="relative z-10 mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-[2rem] bg-blue-500/20">
              <Lock className="h-10 w-10 text-blue-400" />
            </div>
            <h2 className="relative z-10 mb-2 text-3xl font-black tracking-tighter uppercase italic">
              Acceso Restringido
            </h2>
            <p className="relative z-10 mb-8 text-sm font-medium text-slate-400">
              El panel de Adquisición y Gestión de Meta Ads es una
              característica exclusiva del plan{' '}
              <span className="font-bold text-white">Firma Elite</span>.
            </p>
            <Button className="relative z-10 h-14 w-full rounded-2xl bg-blue-600 font-black tracking-widest text-white uppercase hover:bg-blue-500">
              Mejorar a Firma Elite
            </Button>
          </Card>
        </main>
      </div>
    );
  }

  const isDiscoveryCompleted = localMarketingConfig?.discovery?.isCompleted;
  const currentStepData = STEPS[currentStep - 1];
  const StepIcon = currentStepData.icon;

  return (
    <div className="bg-muted/20 min-h-screen pb-32">
      <main className="mx-auto max-w-2xl space-y-6 p-4 pt-6">
        <PageHeader 
          title="Marketing"
          subtitle="Meta Ads"
          showBack={true}
        />
        <header className="space-y-2 pb-4 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-[2rem] bg-blue-500/10">
            <Target className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-black tracking-tighter uppercase italic">
            Control de Adquisición
          </h1>
          <p className="text-muted-foreground text-xs leading-relaxed font-bold tracking-widest uppercase">
            Ecosistema de Crecimiento Elite
          </p>
        </header>

        {/* STEP NAVIGATION */}
        <div className="bg-card mx-auto flex max-w-md rounded-[2rem] p-1 shadow-sm">
          <button
            onClick={() => setView('discovery')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-[1.5rem] py-3 text-[10px] font-black tracking-widest uppercase transition-all ${
              view === 'discovery'
                ? 'bg-primary text-primary-foreground shadow-md'
                : 'text-muted-foreground hover:bg-secondary/50'
            }`}
          >
            {isDiscoveryCompleted && <CheckCircle2 className="h-3 w-3" />}
            1. Auditoría
          </button>
          <button
            disabled={!isDiscoveryCompleted}
            onClick={() => setView('budget')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-[1.5rem] py-3 text-[10px] font-black tracking-widest uppercase transition-all ${
              view === 'budget'
                ? 'bg-slate-900 text-white shadow-md'
                : 'text-muted-foreground hover:bg-secondary/50'
            } disabled:cursor-not-allowed disabled:opacity-50`}
          >
            2. Inversión
          </button>
        </div>

        {view === 'discovery' && (
          <div className="animate-in fade-in slide-in-from-bottom-4 space-y-6 duration-500">
            <div className="space-y-2 px-4">
              <div className="mb-1 flex items-end justify-between">
                <span className="text-primary text-[10px] font-black tracking-[0.2em] uppercase">
                  Paso {currentStep} de {STEPS.length}
                </span>
                <span className="text-muted-foreground text-[10px] font-black tracking-[0.2em] uppercase">
                  {Math.round((currentStep / STEPS.length) * 100)}% Completado
                </span>
              </div>
              <Progress
                value={(currentStep / STEPS.length) * 100}
                className="bg-secondary h-2 rounded-full"
              />
            </div>

            <Card className="overflow-hidden rounded-[2.5rem] border-none pt-0 shadow-2xl">
              <CardHeader className="bg-slate-900 p-8 text-white">
                <div className="flex items-center gap-4">
                  <div className="bg-primary/20 text-primary flex h-12 w-12 items-center justify-center rounded-2xl">
                    <StepIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-black tracking-tight uppercase italic">
                      {currentStepData.title}
                    </CardTitle>
                    <CardDescription className="text-xs font-bold tracking-wider text-slate-400 uppercase">
                      {currentStepData.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-8">
                <div className="space-y-8">
                  {currentStep === 1 && (
                    <div className="animate-in fade-in slide-in-from-right-4 space-y-6">
                      <div className="space-y-3">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          Nombre del Negocio
                        </Label>
                        <Input
                          {...regDiscovery('businessName')}
                          placeholder="Tu marca o establecimiento"
                          className="bg-secondary/30 h-14 rounded-2xl border-none px-6"
                        />
                        {errDiscovery.businessName && (
                          <p className="ml-2 text-[10px] font-bold text-red-500">
                            {errDiscovery.businessName.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          Nicho Principal
                        </Label>
                        <Input
                          {...regDiscovery('niche')}
                          placeholder="Ej: Barbería clásica, Clínica de estética..."
                          className="bg-secondary/30 h-14 rounded-2xl border-none px-6"
                        />
                        {errDiscovery.niche && (
                          <p className="ml-2 text-[10px] font-bold text-red-500">
                            {errDiscovery.niche.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          Ticket Promedio (USD)
                        </Label>
                        <div className="relative">
                          <DollarSign className="absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 text-slate-400" />
                          <Input
                            type="number"
                            {...regDiscovery('averageTicket', {
                              valueAsNumber: true,
                            })}
                            className="bg-secondary/30 h-14 rounded-2xl border-none pr-6 pl-12"
                          />
                        </div>
                        {errDiscovery.averageTicket && (
                          <p className="ml-2 text-[10px] font-bold text-red-500">
                            {errDiscovery.averageTicket.message}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {currentStep === 2 && (
                    <div className="animate-in fade-in slide-in-from-right-4 space-y-6">
                      <div className="space-y-3">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          Frecuencia de Retorno
                        </Label>
                        <p className="text-muted-foreground -mt-2 text-[10px]">
                          ¿Cada cuánto tiempo vuelve un cliente?
                        </p>
                        <Input
                          {...regDiscovery('returnFrequency')}
                          placeholder="Ej: 15, 30 días, Mensual..."
                          className="bg-secondary/30 h-14 rounded-2xl border-none px-6"
                        />
                        {errDiscovery.returnFrequency && (
                          <p className="ml-2 text-[10px] font-bold text-red-500">
                            {errDiscovery.returnFrequency.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          Capacidad Operativa
                        </Label>
                        <p className="text-muted-foreground -mt-2 text-[10px]">
                          ¿Cuántos clientes nuevos puedes atender por semana?
                        </p>
                        <Input
                          {...regDiscovery('capacityCeiling')}
                          placeholder="Ej: 10, 20..."
                          className="bg-secondary/30 h-14 rounded-2xl border-none px-6"
                        />
                        {errDiscovery.capacityCeiling && (
                          <p className="ml-2 text-[10px] font-bold text-red-500">
                            {errDiscovery.capacityCeiling.message}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {currentStep === 3 && (
                    <div className="animate-in fade-in slide-in-from-right-4 space-y-6">
                      <div className="space-y-3">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          El Cliente Ideal
                        </Label>
                        <p className="text-muted-foreground -mt-2 text-[10px]">
                          Describe a quien paga sin quejas y valora tu trabajo.
                        </p>
                        <Textarea
                          {...regDiscovery('idealClient')}
                          className="bg-secondary/30 min-h-[100px] rounded-2xl border-none p-6"
                        />
                        {errDiscovery.idealClient && (
                          <p className="ml-2 text-[10px] font-bold text-red-500">
                            {errDiscovery.idealClient.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          El "Anti-Cliente"
                        </Label>
                        <p className="text-muted-foreground -mt-2 text-[10px]">
                          ¿A quién NO quieres atender bajo ninguna
                          circunstancia?
                        </p>
                        <Textarea
                          {...regDiscovery('antiClient')}
                          className="bg-secondary/30 min-h-[100px] rounded-2xl border-none p-6"
                        />
                        {errDiscovery.antiClient && (
                          <p className="ml-2 text-[10px] font-bold text-red-500">
                            {errDiscovery.antiClient.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          El Disparador (Catalyst)
                        </Label>
                        <p className="text-muted-foreground -mt-2 text-[10px]">
                          ¿Qué evento hace que decidan buscarte JUSTO AHORA?
                        </p>
                        <Textarea
                          {...regDiscovery('catalyst')}
                          placeholder="Ej: Una fiesta el fin de semana, dolor agudo, una ruptura..."
                          className="bg-secondary/30 min-h-[100px] rounded-2xl border-none p-6"
                        />
                        {errDiscovery.catalyst && (
                          <p className="ml-2 text-[10px] font-bold text-red-500">
                            {errDiscovery.catalyst.message}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {currentStep === 4 && (
                    <div className="animate-in fade-in slide-in-from-right-4 space-y-6">
                      <div className="space-y-3">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          Experiencias Previas Malas
                        </Label>
                        <p className="text-muted-foreground -mt-2 text-[10px]">
                          ¿Qué les han hecho otros que les ha causado "trauma"?
                        </p>
                        <Textarea
                          {...regDiscovery('priorTrauma')}
                          className="bg-secondary/30 min-h-[80px] rounded-2xl border-none p-6"
                        />
                        {errDiscovery.priorTrauma && (
                          <p className="ml-2 text-[10px] font-bold text-red-500">
                            {errDiscovery.priorTrauma.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          Miedo Inconfesable
                        </Label>
                        <p className="text-muted-foreground -mt-2 text-[10px]">
                          ¿Qué es lo que realmente les da miedo perder o sentir?
                        </p>
                        <Textarea
                          {...regDiscovery('secretFear')}
                          className="bg-secondary/30 min-h-[80px] rounded-2xl border-none p-6"
                        />
                        {errDiscovery.secretFear && (
                          <p className="ml-2 text-[10px] font-bold text-red-500">
                            {errDiscovery.secretFear.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          La Gran Objeción
                        </Label>
                        <p className="text-muted-foreground -mt-2 text-[10px]">
                          ¿Qué les impide agendar de inmediato?
                        </p>
                        <Textarea
                          {...regDiscovery('mainObjection')}
                          className="bg-secondary/30 min-h-[80px] rounded-2xl border-none p-6"
                        />
                        {errDiscovery.mainObjection && (
                          <p className="ml-2 text-[10px] font-bold text-red-500">
                            {errDiscovery.mainObjection.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          La Competencia
                        </Label>
                        <p className="text-muted-foreground -mt-2 text-[10px]">
                          ¿Por qué algunos eligen a otros en lugar de a ti?
                        </p>
                        <Textarea
                          {...regDiscovery('competition')}
                          className="bg-secondary/30 min-h-[80px] rounded-2xl border-none p-6"
                        />
                        {errDiscovery.competition && (
                          <p className="ml-2 text-[10px] font-bold text-red-500">
                            {errDiscovery.competition.message}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {currentStep === 5 && (
                    <div className="animate-in fade-in slide-in-from-right-4 space-y-6">
                      <div className="space-y-3">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          La Promesa Principal
                        </Label>
                        <p className="text-muted-foreground -mt-2 text-[10px]">
                          ¿Qué transformación garantizas al cliente?
                        </p>
                        <Textarea
                          {...regDiscovery('corePromise')}
                          className="bg-secondary/30 min-h-[100px] rounded-2xl border-none p-6"
                        />
                        {errDiscovery.corePromise && (
                          <p className="ml-2 text-[10px] font-bold text-red-500">
                            {errDiscovery.corePromise.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          Tu Valor Único
                        </Label>
                        <p className="text-muted-foreground -mt-2 text-[10px]">
                          ¿Qué tienes TÚ que nadie más ofrece?
                        </p>
                        <Textarea
                          {...regDiscovery('uniqueValue')}
                          className="bg-secondary/30 min-h-[100px] rounded-2xl border-none p-6"
                        />
                        {errDiscovery.uniqueValue && (
                          <p className="ml-2 text-[10px] font-bold text-red-500">
                            {errDiscovery.uniqueValue.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          Problema del Proceso Actual
                        </Label>
                        <p className="text-muted-foreground -mt-2 text-[10px]">
                          ¿Qué falla hoy en cómo recibes a tus clientes?
                        </p>
                        <Textarea
                          {...regDiscovery('currentProblem')}
                          className="bg-secondary/30 min-h-[100px] rounded-2xl border-none p-6"
                        />
                        {errDiscovery.currentProblem && (
                          <p className="ml-2 text-[10px] font-bold text-red-500">
                            {errDiscovery.currentProblem.message}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {currentStep === 6 && (
                    <div className="animate-in fade-in slide-in-from-right-4 space-y-8">
                      <div className="space-y-4">
                        <Label className="text-[11px] font-bold tracking-wider uppercase">
                          Expectativa de Resultados
                        </Label>
                        <p className="text-muted-foreground -mt-2 text-[10px]">
                          ¿Cuándo esperas ver el retorno de inversión?
                        </p>
                        <Controller
                          control={ctrlDiscovery}
                          name="timeExpectation"
                          render={({ field }) => (
                            <RadioGroup
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              className="flex flex-col gap-4"
                            >
                              <div
                                className={`flex items-start space-x-3 rounded-2xl border p-5 transition-all ${field.value === 'urgent' ? 'border-rose-200 bg-rose-50' : 'border-slate-100 bg-slate-50'}`}
                              >
                                <RadioGroupItem
                                  value="urgent"
                                  id="t1"
                                  className="mt-1"
                                />
                                <Label
                                  htmlFor="t1"
                                  className="text-xs leading-relaxed font-medium"
                                >
                                  "En 2 o 3 días. Necesito ventas urgentes."
                                </Label>
                              </div>
                              <div
                                className={`flex items-start space-x-3 rounded-2xl border p-5 transition-all ${field.value === 'realistic' ? 'border-emerald-200 bg-emerald-50' : 'border-slate-100 bg-slate-50'}`}
                              >
                                <RadioGroupItem
                                  value="realistic"
                                  id="t2"
                                  className="mt-1"
                                />
                                <Label
                                  htmlFor="t2"
                                  className="text-xs leading-relaxed font-medium"
                                >
                                  "Entiendo que la primera semana es de prueba y
                                  en 14-30 días vemos flujo real."
                                </Label>
                              </div>
                            </RadioGroup>
                          )}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex gap-4 pt-4">
                    {currentStep > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={prevStep}
                        className="h-16 flex-1 rounded-2xl text-[10px] font-black tracking-[0.2em] uppercase"
                      >
                        Anterior
                      </Button>
                    )}

                    {currentStep < STEPS.length ? (
                      <Button
                        type="button"
                        onClick={nextStep}
                        className="h-16 flex-[2] rounded-2xl text-[10px] font-black tracking-[0.2em] uppercase shadow-xl"
                      >
                        Siguiente
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        disabled={isSubmittingDiscovery}
                        onClick={handleSubDiscovery(onDiscoverySubmit)}
                        className="h-16 flex-[2] rounded-2xl bg-emerald-600 text-[10px] font-black tracking-[0.2em] text-white uppercase shadow-xl hover:bg-emerald-500"
                      >
                        {isSubmittingDiscovery ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          'Finalizar Auditoría'
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {view === 'budget' && (
          <div className="animate-in fade-in slide-in-from-right-8 space-y-6">
            {marketingConfig?.isManaged ? (
              <Card className="rounded-[2.5rem] border-none border-blue-100 bg-blue-50 p-10 text-center shadow-2xl">
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-[2.5rem] bg-blue-600 shadow-xl shadow-blue-200">
                  <Star className="h-10 w-10 fill-white text-white" />
                </div>
                <h3 className="mb-3 text-xl font-black tracking-tight text-blue-900 uppercase italic">
                  Ecosistema Elite Activo 💎
                </h3>
                <p className="mx-auto max-w-sm text-sm leading-relaxed font-bold text-blue-800/70">
                  Tu estrategia y presupuesto de Meta Ads están siendo gestionados
                  directamente por el equipo de expertos de Tufirma.Pro. 
                  Explosión de agendamientos en progreso.
                </p>
                <div className="mt-8 flex items-center justify-center gap-3 rounded-2xl bg-white/50 p-4 border border-blue-100">
                  <ShieldCheck className="h-5 w-5 text-blue-600" />
                  <span className="text-[10px] font-black tracking-widest text-blue-700 uppercase">
                    Configuración Protegida por Admin
                  </span>
                </div>
              </Card>
            ) : !marketingConfig?.isAdsEnabled ? (
              <Card className="rounded-[2.5rem] border-none border-amber-100 bg-amber-50 p-8 text-center shadow-xl">
                <AlertCircle className="mx-auto mb-4 h-12 w-12 text-amber-500" />
                <h3 className="mb-2 text-lg font-black tracking-tight text-amber-800 uppercase italic">
                  Auditoría en Revisión
                </h3>
                <p className="mx-auto max-w-sm text-xs leading-relaxed font-medium text-amber-700/80">
                  Hemos recibido tu formulario de descubrimiento. Nuestro equipo
                  de expertos está analizando tu caso para diseñar la estrategia
                  exacta. Te notificaremos cuando puedas autorizar tu
                  presupuesto de Meta Ads.
                </p>
                <Button
                  onClick={() => setView('discovery')}
                  variant="outline"
                  className="mt-6 rounded-xl border-amber-200 text-[10px] font-black tracking-widest text-amber-700 uppercase hover:bg-amber-100"
                >
                  <ChevronLeft className="mr-1 h-4 w-4" /> Ver mis respuestas
                </Button>
              </Card>
            ) : (
              <Card className="overflow-hidden rounded-[2.5rem] border-none pt-0 shadow-xl">
                <CardHeader className="bg-slate-900 p-8 text-white">
                  <CardTitle className="flex items-center gap-3 text-lg font-black tracking-widest uppercase italic">
                    <TrendingUp className="h-6 w-6 text-emerald-400" />
                    Orden de Inversión
                  </CardTitle>
                  <CardDescription className="text-xs font-bold text-slate-400">
                    Tu estrategia está lista. Autoriza el presupuesto para
                    encender las campañas.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-8 p-8">
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <Label className="ml-1 text-[11px] font-black tracking-widest text-slate-500 uppercase">
                        Presupuesto Estimado (Estimate)
                      </Label>
                      <div className="relative">
                        <DollarSign className="absolute top-1/2 left-4 h-6 w-6 -translate-y-1/2 text-slate-400" />
                        <Input
                          type="number"
                          {...regAds('adsBudget', {
                            valueAsNumber: true,
                            min: 0,
                          })}
                          placeholder="0.00"
                          className="focus-visible:ring-primary h-16 rounded-[1.5rem] border-none bg-slate-100 pl-14 text-2xl font-black shadow-inner"
                        />
                      </div>
                      <p className="ml-1 text-[10px] font-bold text-slate-400">
                        Este monto es solo una estimación para diseñar tu plan
                        de anuncios. El presupuesto real se definirá durante la
                        estrategia.
                      </p>
                    </div>

                    <Separator className="my-6 opacity-50" />

                    {true ? (
                      <div className="space-y-5">
                        <div className="bg-primary/5 border-primary/20 hover:bg-primary/10 space-y-3 rounded-[1.5rem] border p-5 transition-colors">
                          <div className="flex items-start gap-4">
                            <Controller
                              control={ctrlAds}
                              name="acceptedPolicy"
                              rules={{ required: true }}
                              render={({ field }) => (
                                <Checkbox
                                  id="policy"
                                  className="border-primary data-[state=checked]:bg-primary mt-1 h-5 w-5 rounded-md"
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              )}
                            />
                            <div className="space-y-1">
                              <Label
                                htmlFor="policy"
                                className="cursor-pointer text-[12px] leading-snug font-bold text-slate-700"
                              >
                                Entiendo que Meta cobrará este monto
                                directamente a mi tarjeta y declaro haber leído,
                                entendido y aceptado las{' '}
                                <a
                                  href={latestPolicy.pdfUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:text-primary/80 inline-flex items-center gap-1 font-black underline transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Condiciones de Gestión Meta Ads (
                                  {latestPolicy.versionTag})
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              </Label>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-center gap-2 py-2">
                          <ShieldCheck className="h-4 w-4 text-emerald-500" />
                          <span className="text-[9px] font-black tracking-widest text-emerald-600 uppercase">
                            Firma Digital Habilitada (Auditoría IP)
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-3 rounded-2xl border border-amber-100 bg-amber-50 p-4">
                        <AlertCircle className="h-5 w-5 shrink-0 text-amber-500" />
                        <p className="text-[10px] leading-snug font-bold text-amber-700">
                          Cargando contrato legal...
                        </p>
                      </div>
                    )}
                  </div>

                  <Button
                    disabled={
                      !isValidAds ||
                      !acceptedPolicy ||
                      isSubmittingAds ||
                      !latestPolicy
                    }
                    onClick={handleSubAds(onAdsSubmit)}
                    className="h-16 w-full rounded-[2rem] bg-slate-900 text-xs font-black tracking-[0.2em] text-white uppercase shadow-xl transition-all hover:bg-slate-800 active:scale-95 disabled:opacity-50"
                  >
                    {isSubmittingAds ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      'Autorizar Ejecución de Pauta'
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Info Legal Adicional */}
            <div className="space-y-4 px-6">
              <div className="flex items-center justify-center gap-2">
                <ShieldCheck className="h-4 w-4 text-slate-400" />
                <h3 className="text-[10px] font-black tracking-widest text-slate-400 uppercase">
                  Transparencia Operativa
                </h3>
              </div>
              <ul className="mx-auto max-w-sm space-y-3">
                {[
                  'Obligación de Medios: Optimizamos campañas profesionalmente pero los resultados exactos dependen del mercado.',
                  'Separación de Fondos: Tu pago por anuncios va íntegro a Meta. Nosotros solo cobramos nuestro SaaS Fee.',
                  'Compromiso 30 Días: El algoritmo requiere tiempo para calibrar y entregar citas de alto valor.',
                ].map((text, i) => (
                  <li
                    key={i}
                    className="text-center text-[10px] leading-relaxed font-medium text-slate-500 italic"
                  >
                    {text}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
